import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanDeactivate,
    RouterStateSnapshot,
} from '@angular/router';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../app.config';

import { ModelTransformer } from './common.model';
import { mapList } from './rest.operators';

export interface ExtText {
    initials: string;
    shortName: string;
    name: string;
}

interface ExtBase {
    id: number;
    text: ExtText;
}

interface ExtHierarchyType extends ExtBase {}

interface ExtSystemIdType extends ExtBase {}

interface ExtSecurityGroup {
    securityGroupId: number;
    name: string;
}

export interface ExtRole extends ExtBase {}

interface ExtSecurityAssociationType {
    securityAssociationTypeId: number;
    name: string;
}

interface Base {
    id: number;
    label: string;
}

export enum Status {
    ALL = -1,
    NOT_ACTIVE,
    ACTIVE,
}

export interface HierarchyType extends Base {}

export interface SystemIdType extends Base {}

export interface SecurityGroup extends Base {}

export interface SuperGroup extends Base {}

export interface Role extends Base {}

export interface Site extends Base {}

export interface EmployeeType extends Base {}

export interface SecurityAssociationType extends Base {}

abstract class BaseModelSetTransformer
    implements ModelTransformer<Base[], ExtBase[]>
{
    transform(model: ExtBase[]): Base[] {
        const transformed: Base[] = [];
        model.forEach((item) => {
            transformed.push({
                id: item.id,
                label: item.text.name,
            });
        });
        return transformed;
    }

    reverseTransform(model: Base[]): ExtBase[] {
        return null;
    }
}

class HierarchyTypeModelSetTransformer extends BaseModelSetTransformer {}

class SystemIdTypeModelSetTransformer extends BaseModelSetTransformer {}

class SecurityGroupModelSetTransformer
    implements ModelTransformer<SecurityGroup[], ExtSecurityGroup[]>
{
    transform(model: ExtSecurityGroup[]): SecurityGroup[] {
        const transformed: SecurityGroup[] = [];
        model.forEach((item) => {
            transformed.push({
                id: item.securityGroupId,
                label: item.name,
            });
        });
        return transformed;
    }

    reverseTransform(model: SecurityGroup[]): ExtSecurityGroup[] {
        return null;
    }
}

class SuperGroupModelSetTransformer extends BaseModelSetTransformer {}

class RoleModelSetTransformer extends BaseModelSetTransformer {}

class SiteModelSetTransformer extends BaseModelSetTransformer {}

class EmployeeTypeModelSetTransformer extends BaseModelSetTransformer {}

class SecurityAssociationTypeModelSetTransformer
    implements
        ModelTransformer<
            SecurityAssociationType[],
            ExtSecurityAssociationType[]
        >
{
    transform(model: ExtSecurityAssociationType[]): SecurityAssociationType[] {
        const transformed: SecurityAssociationType[] = [];
        model.forEach((item) => {
            transformed.push({
                id: item.securityAssociationTypeId,
                label: item.name,
            });
        });
        return transformed;
    }

    reverseTransform(
        model: SecurityAssociationType[],
    ): ExtSecurityAssociationType[] {
        return null;
    }
}

@Injectable()
export class CommonService {
    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {}

    listHierarchyTypes(): Observable<HierarchyType[]> {
        return this._http
            .get(`${this._appConfig.API_ENDPOINTS.USER_PROFILE}/HierarchyType`)
            .pipe(
                map((r) =>
                    new HierarchyTypeModelSetTransformer().transform(
                        r as ExtHierarchyType[],
                    ),
                ),
                catchError((error) => {
                    console.error(error);
                    throw new Error('HierarchyType failed');
                }),
            );
    }

    listSystemIdTypes(): Observable<SystemIdType[]> {
        return this._http
            .get(`${this._appConfig.API_ENDPOINTS.USER_PROFILE}/SystemIdType`)
            .pipe(
                map((r) =>
                    new SystemIdTypeModelSetTransformer().transform(
                        r as ExtSystemIdType[],
                    ),
                ),
                catchError((error) => {
                    console.error(error);
                    throw new Error('SystemIdType failed');
                }),
            );
    }

    listSecurityGroups(): Observable<SecurityGroup[]> {
        return this._http
            .get(`${this._appConfig.API_ENDPOINTS.USER}/SecurityGroup`)
            .pipe(
                map((r) =>
                    new SecurityGroupModelSetTransformer().transform(
                        r as ExtSecurityGroup[],
                    ),
                ),
                catchError((error) => {
                    console.error(error);
                    throw new Error('SecurityGroup failed');
                }),
            );
    }

    listSuperGroups(searchText?: string): Observable<SuperGroup[]> {
        let params = new HttpParams().set('status', Status.ACTIVE.toString());
        if (searchText) {
            params = params.set('searchText', searchText);
        }

        return this._http
            .get(`${this._appConfig.API_ENDPOINTS.USER_PROFILE}/SuperGroup`, {
                params,
            })
            .pipe(
                mapList(new SuperGroupModelSetTransformer()),
                catchError((error) => {
                    console.error(error);
                    throw new Error('SuperGroup failed');
                }),
            );
    }

    listRoles(): Observable<Role[]> {
        const params = new HttpParams().set('status', Status.ACTIVE.toString());

        return this._http
            .get(`${this._appConfig.API_ENDPOINTS.USER_PROFILE}/role`, {
                params,
            })
            .pipe(
                mapList(new RoleModelSetTransformer()),
                catchError((error) => {
                    console.error(error);
                    throw new Error('role failed');
                }),
            );
    }

    listSites(): Observable<Site[]> {
        const params = new HttpParams().set('status', Status.ACTIVE.toString());

        return this._http
            .get(`${this._appConfig.API_ENDPOINTS.USER_PROFILE}/site`, {
                params,
            })
            .pipe(
                mapList(new SiteModelSetTransformer()),
                catchError((error) => {
                    console.error(error);
                    throw new Error('site failed');
                }),
            );
    }

    listEmployeeTypes(): Observable<EmployeeType[]> {
        return this._http
            .get(`${this._appConfig.API_ENDPOINTS.USER_PROFILE}/EmployeeType`)
            .pipe(
                mapList(new EmployeeTypeModelSetTransformer()),
                catchError((error) => {
                    console.error(error);
                    throw new Error('EmployeeType failed');
                }),
            );
    }

    listSecurityAssociationTypes(): Observable<SecurityAssociationType[]> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.USER}/lookups/SecurityAssociationTypes`,
            )
            .pipe(
                map((r) =>
                    new SecurityAssociationTypeModelSetTransformer().transform(
                        r as ExtSecurityAssociationType[],
                    ),
                ),
                catchError((error) => {
                    console.error(error);
                    throw new Error('SecurityAssociationTypes failed');
                }),
            );
    }
}

// TODO move
// TODO refactor. use router.CanDeactivate interface directly?
export interface CanComponentDeactivate {
    canDeactivate: (
        component: CanComponentDeactivate,
        route: ActivatedRouteSnapshot,
        currentState?: RouterStateSnapshot,
        nextState?: RouterStateSnapshot,
    ) => Observable<boolean> | Promise<boolean> | boolean;
}

@Injectable()
export class CanDeactivateService
    implements CanDeactivate<CanComponentDeactivate>
{
    canDeactivate(
        component: CanComponentDeactivate,
        route: ActivatedRouteSnapshot,
        currentState?: RouterStateSnapshot,
        nextState?: RouterStateSnapshot,
    ): Observable<boolean> | Promise<boolean> | boolean {
        return !component.canDeactivate
            ? true
            : component.canDeactivate(
                  component,
                  route,
                  currentState,
                  nextState,
              );
    }
}
